import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, RefreshCw, Scale } from "lucide-react";
import React, { useState } from "react";

import { getCashflowReceivablesCheck } from "@/api/cashflow";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ALL_ORGS,
  CELL_PAD,
  NUM_CELL,
  diffDecimalStrings,
  diffDecimalStringsNonNegative,
  errorDetail,
  formatRubCompact,
  orgLabel,
} from "@/pages/cashflow/utils";
import type {
  CashflowOrg,
  CashflowReceivablesCheckDoc,
  CashflowReceivablesCheckKontragent,
  CashflowReceivablesCheckOrg,
} from "@/types/api";

interface Props {
  /** Дата расчёта — та же, что у календаря (`as_of` из его ОТВЕТА). */
  asOf: string;
  orgs: CashflowOrg[];
  /** Вкладка страницы: `"*"` — все организации, иначе ИНН одной. */
  orgInn: string;
  /** Активное подключение к 1С — в ключе кэша, как у календаря. */
  connectionId: string | null;
}

const BADGES: Record<string, string> = {
  no_plan: "bg-amber-100 text-amber-800",
  planned: "bg-emerald-100 text-emerald-800",
  overdue: "bg-rose-100 text-rose-800",
  partial: "bg-amber-100 text-amber-800",
};

const BADGE_LABELS: Record<string, string> = {
  no_plan: "Без плана",
  planned: "В плане",
  overdue: "Просрочен",
  partial: "Частично",
};

/** Бейдж статуса документа; незнакомый статус показываем как есть, а не прячем. */
function StatusBadge({ status }: { status: string }): React.ReactElement {
  const cls = BADGES[status] ?? "bg-muted text-foreground";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] ${cls}`}>
      {BADGE_LABELS[status] ?? status}
    </span>
  );
}

/** Есть ли по контрагенту работа: долг без плана или просроченный план. */
function hasProblem(k: CashflowReceivablesCheckKontragent): boolean {
  // Нечитаемая сумма (рассинхрон с бэком) — тоже «проблема»: прятать её нельзя.
  return Number(k.unplanned) !== 0 || Number(k.overdue_planned) !== 0;
}

function DocRow({ doc }: { doc: CashflowReceivablesCheckDoc }): React.ReactElement {
  // «Без плана» документа бэк не отдаёт — считаем сами, без float (как diffDecimalStrings).
  const unplanned = diffDecimalStringsNonNegative(
    diffDecimalStrings(doc.debt, doc.planned),
    doc.overdue_planned,
  );
  return (
    <TableRow className="text-muted-foreground">
      <TableCell className={`${CELL_PAD} pl-8`}>{doc.label}</TableCell>
      <TableCell className={`${NUM_CELL} ${CELL_PAD}`}>{formatRubCompact(doc.debt)}</TableCell>
      <TableCell className={`${NUM_CELL} ${CELL_PAD}`}>
        {formatRubCompact(doc.planned)}
      </TableCell>
      <TableCell className={`${NUM_CELL} ${CELL_PAD}`}>
        {formatRubCompact(doc.overdue_planned)}
      </TableCell>
      <TableCell className={`${NUM_CELL} ${CELL_PAD}`}>{formatRubCompact(unplanned)}</TableCell>
      <TableCell className={CELL_PAD}>
        <StatusBadge status={doc.status} />
      </TableCell>
    </TableRow>
  );
}

function KontragentRows({
  kontragent,
  expanded,
  onToggle,
}: {
  kontragent: CashflowReceivablesCheckKontragent;
  expanded: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const problem = hasProblem(kontragent);
  const unplanned = Number(kontragent.unplanned);
  return (
    <>
      <TableRow className={problem ? "" : "text-muted-foreground"}>
        <TableCell className={CELL_PAD}>
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`${expanded ? "Свернуть" : "Раскрыть"} ${kontragent.name}`}
            onClick={onToggle}
            className="inline-flex items-center gap-1 rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Chevron className="h-3 w-3 shrink-0" aria-hidden="true" />
            {kontragent.name}
          </button>
          {kontragent.inn !== null && (
            <span className="block pl-4 text-[11px] text-muted-foreground">
              {kontragent.inn}
            </span>
          )}
        </TableCell>
        <TableCell className={`${NUM_CELL} ${CELL_PAD}`}>
          {formatRubCompact(kontragent.debt)}
        </TableCell>
        <TableCell className={`${NUM_CELL} ${CELL_PAD}`}>
          {formatRubCompact(kontragent.planned)}
        </TableCell>
        <TableCell className={`${NUM_CELL} ${CELL_PAD}`}>
          {formatRubCompact(kontragent.overdue_planned)}
        </TableCell>
        <TableCell
          className={`${NUM_CELL} ${CELL_PAD} ${unplanned !== 0 ? "font-semibold" : ""}`}
        >
          {formatRubCompact(kontragent.unplanned)}
        </TableCell>
        <TableCell className={CELL_PAD} />
      </TableRow>
      {expanded && kontragent.docs.map((d) => <DocRow key={d.ref} doc={d} />)}
    </>
  );
}

function OrgBlock({
  org,
  orgs,
  withHeading,
  onlyProblems,
  expanded,
  onToggle,
}: {
  org: CashflowReceivablesCheckOrg;
  orgs: CashflowOrg[];
  withHeading: boolean;
  onlyProblems: boolean;
  /** Ключи раскрытых контрагентов, `${org_inn}|${ref}` (см. `toggleKontragent`). */
  expanded: Set<string>;
  onToggle: (key: string) => void;
}): React.ReactElement {
  const shown = onlyProblems ? org.kontragents.filter(hasProblem) : org.kontragents;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {withHeading && (
          <span className="text-sm font-medium">{orgLabel(org.org_inn, orgs)}</span>
        )}
        <span className="text-xs text-muted-foreground">
          Долг <span className="tabular-nums">{formatRubCompact(org.debt)}</span> · В плане{" "}
          <span className="tabular-nums">{formatRubCompact(org.planned)}</span> · Просрочено в
          плане{" "}
          <span className="tabular-nums">{formatRubCompact(org.overdue_planned)}</span> · Без
          плана{" "}
          <span className="font-semibold tabular-nums">{formatRubCompact(org.unplanned)}</span>
        </span>
      </div>

      {shown.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {onlyProblems
            ? "Контрагентов с долгом без плана или с просроченным планом нет"
            : "Долга покупателей по регистру нет"}
        </p>
      ) : (
        <Table className="text-xs">
          <TableHeader>
            <TableRow>
              <TableHead className={`${CELL_PAD} h-8`}>Контрагент</TableHead>
              <TableHead className={`${CELL_PAD} h-8 text-right`}>Долг</TableHead>
              <TableHead className={`${CELL_PAD} h-8 text-right`}>В плане</TableHead>
              <TableHead className={`${CELL_PAD} h-8 text-right`}>Просрочено</TableHead>
              <TableHead className={`${CELL_PAD} h-8 text-right`}>Без плана</TableHead>
              <TableHead className={`${CELL_PAD} h-8`}>Статус</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((k) => {
              // Один контрагент может быть покупателем у двух наших юрлиц — ref
              // одинаковый, а раскрытие должно различаться по организации.
              const key = `${org.org_inn}|${k.ref}`;
              return (
                <KontragentRows
                  key={k.ref}
                  kontragent={k}
                  expanded={expanded.has(key)}
                  onToggle={() => onToggle(key)}
                />
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/**
 * Панель «Дебиторка vs план прихода» — список «кого пнуть».
 *
 * Запрос тяжёлый (регистр расчётов + планы поступлений по всем организациям,
 * несколько секунд), поэтому панель по умолчанию свёрнута и грузится только по
 * раскрытию (`enabled`). Ошибку показываем баннером с «Повторить» — пустая или
 * прошлая таблица вместо причины была бы враньём.
 */
export function ReceivablesCheckPanel({
  asOf,
  orgs,
  orgInn,
  connectionId,
}: Props): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ["cashflow-receivables-check", connectionId ?? "none", asOf],
    queryFn: () => getCashflowReceivablesCheck(asOf),
    enabled: open,
  });

  // При ошибке react-query держит прошлый ответ — показывать его рядом с
  // баннером нельзя: это старая сверка, выданная за пересчитанную.
  const data = q.isError ? undefined : q.data;
  const shownOrgs =
    data === undefined
      ? []
      : orgInn === ALL_ORGS
        ? data.orgs
        : data.orgs.filter((o) => o.org_inn === orgInn);
  const Chevron = open ? ChevronDown : ChevronRight;

  // Ключ раскрытия — `org_inn|ref`, а не только `ref`: один и тот же покупатель
  // может быть контрагентом у двух наших юрлиц, и раскрытие не должно
  // расползаться между их таблицами.
  const toggleKontragent = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex w-full items-center gap-2 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Chevron className="h-4 w-4 shrink-0" aria-hidden="true" />
            <Scale className="h-4 w-4 shrink-0" aria-hidden="true" />
            Дебиторка vs план прихода
            <span className="text-xs font-normal text-muted-foreground">
              {open ? "" : "запрос тяжёлый — считается по раскрытию"}
            </span>
          </button>
        </CardTitle>
      </CardHeader>

      {open && (
        <CardContent className="space-y-3">
          {q.isError && (
            <div className="space-y-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
              <p>Сверка не посчиталась: {errorDetail(q.error)}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void q.refetch()}
                disabled={q.isFetching}
              >
                <RefreshCw
                  className={`mr-2 h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`}
                />
                Повторить
              </Button>
            </div>
          )}

          {q.isFetching && !q.isError && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
              Считаем сверку…
            </p>
          )}

          {data !== undefined && (
            <>
              <p className="text-xs text-muted-foreground">{data.note}</p>

              <div className="flex items-center gap-2">
                <input
                  id="receivables_check_only_problems"
                  type="checkbox"
                  className="h-4 w-4"
                  checked={onlyProblems}
                  onChange={(e) => setOnlyProblems(e.target.checked)}
                />
                <Label
                  htmlFor="receivables_check_only_problems"
                  className="text-xs font-normal"
                >
                  Показывать только с проблемами (долг без плана или просроченный план)
                </Label>
              </div>

              {shownOrgs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  По выбранной организации ({orgInn}) сверки в ответе нет
                </p>
              ) : (
                shownOrgs.map((org) => (
                  <OrgBlock
                    key={org.org_inn}
                    org={org}
                    orgs={orgs}
                    withHeading={orgInn === ALL_ORGS}
                    onlyProblems={onlyProblems}
                    expanded={expanded}
                    onToggle={toggleKontragent}
                  />
                ))
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
